import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "./auth-context";
import { hasPermission, type StaffRole, useMe } from "./use-permission";

export function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  if (!auth.ready) return <FullPage>Signing in…</FullPage>;
  if (!auth.isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
}

export interface RequireRoleProps {
  /** Permission string like `cms:write`; satisfied by `*` or `cms:*`. */
  perm?: string;
  /** Alternative: explicit allowed roles. */
  roles?: StaffRole[];
}

/** Route guard. The server enforces; this only avoids rendering dead UI. */
export function RequireRole({ perm, roles }: RequireRoleProps) {
  const me = useMe();
  if (me.isPending) return <FullPage>Loading permissions…</FullPage>;
  if (me.isError || !me.data) return <Navigate to="/403" replace />;
  const ok =
    (perm ? hasPermission(me.data.permissions, perm) : true) &&
    (roles ? roles.includes(me.data.role) : true) &&
    me.data.is_active;
  return ok ? <Outlet /> : <Navigate to="/403" replace />;
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center text-muted-foreground text-sm">
      {children}
    </div>
  );
}
